import React from "react";
import { useTranslation } from "react-i18next";
import { Handle, Position, NodeProps } from "reactflow";
import {
  NodeData,
  NodeType,
  ActivityDefinition,
  CustomNodeData,
} from "../../types";
import {
  PlayIcon,
  StopIcon,
  CheckCircleIcon,
  BellIcon,
  CogIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArchiveBoxIcon,
  PresentationChartLineIcon,
  CubeIcon,
  FolderIcon,
  PencilIcon,
  EyeIcon,
  BookmarkIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const getNodeIcon = (type: NodeType, activityIcon?: string) => {
  switch (type) {
    case NodeType.START:
      return <PlayIcon className="w-5 h-5" />;
    case NodeType.END:
      return <StopIcon className="w-5 h-5" />;
    case NodeType.ACTIVITY:
      return <CogIcon className="w-5 h-5" />;
    case NodeType.POOL_ACTIVITY:
      return activityIcon ? (
        <span className="text-lg">{activityIcon}</span>
      ) : (
        <CubeIcon className="w-5 h-5" />
      );
    default:
      return <DocumentTextIcon className="w-5 h-5" />;
  }
};

const getNodeStyle = (
  type: NodeType,
  isSelected: boolean,
  activityColor?: string
) => {
  const baseStyle = `workflow-node ${isSelected ? "selected" : ""}`;

  switch (type) {
    case NodeType.START:
      return `${baseStyle} workflow-node-start`;
    case NodeType.END:
      return `${baseStyle} workflow-node-end`;
    case NodeType.ACTIVITY:
      return `${baseStyle} workflow-node-task`;
    case NodeType.POOL_ACTIVITY:
      return `${baseStyle} workflow-node-pool-activity`;
    default:
      return baseStyle;
  }
};

export const CustomNode: React.FC<NodeProps<CustomNodeData>> = ({
  id,
  data,
  selected = false,
  type,
}) => {
  const { t } = useTranslation(["activities", "workflows"]);
  const nodeType = type as NodeType;

  const handleEdit = () => {
    if (data.onEdit) {
      data.onEdit(id);
    }
  };

  const handleView = () => {
    if (data.onView) {
      data.onView(id);
    }
  };

  const handleSaveAsTemplate = () => {
    if (data.onSaveAsTemplate) {
      data.onSaveAsTemplate(id);
    }
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
  };

  const showTargetHandle = nodeType !== NodeType.START;
  const showSourceHandle = nodeType !== NodeType.END;

  // Activity definition data for pool activities
  const activityDefinition = data.activityDefinition;

  // Check if this is a saved activity (has activityId from TaskFormData or multipleFormsData)
  const isSavedActivity = !!(
    data.taskFormData?.activityId ||
    data.activityDefinitionId ||
    data.taskFormData?.multipleFormsData?.length ||
    (data.taskFormData &&
      data.taskFormData.elements &&
      data.taskFormData.elements.length > 0)
  );

  // Debug log for templates
  if (
    (nodeType === NodeType.ACTIVITY || nodeType === NodeType.POOL_ACTIVITY) &&
    data.onView
  ) {
  }

  // Check activity completion status
  const getActivityStatus = () => {
    if (!isSavedActivity) return "empty"; // Boş activity

    if (data.taskFormData?.multipleFormsData?.length) {
      // Multi-form activity
      const totalForms = data.taskFormData.multipleFormsData.length;
      let filledForms = 0;

      data.taskFormData.multipleFormsData.forEach((formData) => {
        if (
          formData.submissionData &&
          Object.keys(formData.submissionData).length > 0
        ) {
          filledForms++;
        }
      });

      if (filledForms === 0) return "configured"; // Sadece yapılandırılmış
      if (filledForms === totalForms) return "completed"; // Tamamen doldurulmuş
      return "partial"; // Kısmen doldurulmuş
    }

    // Single form activity (legacy)
    if (
      data.taskFormData?.submissionData &&
      Object.keys(data.taskFormData.submissionData).length > 0
    ) {
      return "completed";
    }

    return "configured"; // Yapılandırılmış ama doldurulmamış
  };

  const activityStatus =
    nodeType === NodeType.ACTIVITY || nodeType === NodeType.POOL_ACTIVITY
      ? getActivityStatus()
      : null;

  // Get activity status styling
  const getActivityStyling = () => {
    if (
      (nodeType !== NodeType.ACTIVITY && nodeType !== NodeType.POOL_ACTIVITY) ||
      !activityStatus
    )
      return {};

    switch (activityStatus) {
      case "empty":
        return {
          borderColor: "#d1d5db", // gray-300
          backgroundColor: "#f9fafb", // gray-50
        };
      case "configured":
        return {
          borderColor: "#3b82f6", // blue-500
          backgroundColor: "#eff6ff", // blue-50
        };
      case "partial":
        return {
          borderColor: "#f59e0b", // amber-500
          backgroundColor: "#fffbeb", // amber-50
        };
      case "completed":
        return {
          borderColor: "#10b981", // emerald-500
          backgroundColor: "#ecfdf5", // emerald-50
        };
      default:
        return {};
    }
  };

  const activityStyling = getActivityStyling();

  return (
    <div
      className={`${getNodeStyle(
        nodeType,
        selected,
        activityDefinition?.color
      )} drag-handle group relative`}
      style={{
        borderLeftColor:
          activityDefinition?.color || activityStyling.borderColor,
        borderLeftWidth:
          activityDefinition?.color || activityStatus ? "4px" : undefined,
        backgroundColor: activityStyling.backgroundColor,
        ...activityStyling,
      }}
    >
      {/* Pending Activity Indicator */}
      {data.isPendingActivity && (
        <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full shadow-md font-semibold flex items-center space-x-1 z-10">
          <span>⏳</span>
          <span>Pending</span>
        </div>
      )}

      {showTargetHandle && (
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          className="w-3 h-3 !bg-gray-400"
        />
      )}

      <div className="flex items-center justify-center space-x-2">
        <div className="text-gray-600">
          {getNodeIcon(nodeType, activityDefinition?.icon)}
        </div>
        <div>
          <div className="font-medium text-sm text-gray-900 text-center">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-gray-500 mt-1 text-center">
              {data.description}
            </div>
          )}

          {/* Pool activity specific info */}
          {nodeType === NodeType.POOL_ACTIVITY && activityDefinition && (
            <div className="mt-1 space-y-1 text-center">
              {activityDefinition.activityForms?.length > 0 && (
                <div className="text-xs text-blue-600">
                  📝 {activityDefinition.activityForms.length} form
                  {activityDefinition.activityForms.length !== 1 ? "s" : ""}
                </div>
              )}
              {activityDefinition.attachments &&
                (() => {
                  try {
                    const attachmentArray = JSON.parse(
                      activityDefinition.attachments
                    );
                    if (attachmentArray.length > 0) {
                      return (
                        <div className="text-xs text-green-600">
                          📎 {attachmentArray.length} document
                          {attachmentArray.length !== 1 ? "s" : ""}
                        </div>
                      );
                    }
                  } catch (e) {
                  }
                  return null;
                })()}
              {activityDefinition.estimatedDurationMinutes && (
                <div className="text-xs text-green-600">
                  ⏱️ ~{activityDefinition.estimatedDurationMinutes}min
                </div>
              )}
            </div>
          )}

          {/* Regular activity info */}
          {nodeType === NodeType.ACTIVITY && isSavedActivity && (
            <div className="mt-1 space-y-1 text-center">
              {/* Activity type indicator - pool activity tarzında */}

              {/* Forms info - prioritize fresh activityDefinition over taskFormData */}
              {(() => {
                return null;
              })()}
              {data.activityDefinition?.activityForms?.length ? (
                <div className="text-xs text-blue-600">
                  📝 {data.activityDefinition.activityForms.length} form
                  {data.activityDefinition.activityForms.length !== 1
                    ? "s"
                    : ""}
                  {(() => {
                    const totalForms =
                      data.activityDefinition.activityForms.length;
                    let filledForms = 0;
                    // Check filled forms from taskFormData if available
                    if (data.taskFormData?.multipleFormsData) {
                      data.taskFormData.multipleFormsData.forEach(
                        (formData) => {
                          if (
                            formData.submissionData &&
                            Object.keys(formData.submissionData).length > 0
                          ) {
                            filledForms++;
                          }
                        }
                      );
                    }
                    return filledForms > 0 ? ` (${filledForms} filled)` : "";
                  })()}
                </div>
              ) : data.taskFormData?.multipleFormsData?.length ? (
                <div className="text-xs text-blue-600">
                  📝 {data.taskFormData.multipleFormsData.length} form
                  {data.taskFormData.multipleFormsData.length !== 1 ? "s" : ""}
                  {(() => {
                    const totalForms =
                      data.taskFormData.multipleFormsData.length;
                    let filledForms = 0;
                    data.taskFormData.multipleFormsData.forEach((formData) => {
                      if (
                        formData.submissionData &&
                        Object.keys(formData.submissionData).length > 0
                      ) {
                        filledForms++;
                      }
                    });
                    return filledForms > 0 ? ` (${filledForms} filled)` : "";
                  })()}
                </div>
              ) : data.taskFormData?.elements?.length ? (
                <div className="text-xs text-blue-600">
                  📝 1 form
                  {data.taskFormData.submissionData &&
                  Object.keys(data.taskFormData.submissionData).length > 0
                    ? " (filled)"
                    : ""}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  📝 No forms configured
                </div>
              )}

              {/* Attachments info */}
              {(() => {
                // Check activityDefinition first (fresh data)
                if (data.activityDefinition?.attachments) {
                  try {
                    const attachmentArray = JSON.parse(
                      data.activityDefinition.attachments
                    );
                    if (attachmentArray.length > 0) {
                      return (
                        <div className="text-xs text-green-600">
                          📎 {attachmentArray.length} document
                          {attachmentArray.length !== 1 ? "s" : ""}
                        </div>
                      );
                    }
                  } catch (e) {
                  }
                }

                // Fallback to taskFormData.attachments (legacy format - array)
                if (
                  data.taskFormData?.attachments &&
                  Array.isArray(data.taskFormData.attachments)
                ) {
                  if (data.taskFormData.attachments.length > 0) {
                    return (
                      <div className="text-xs text-green-600">
                        📎 {data.taskFormData.attachments.length} document
                        {data.taskFormData.attachments.length !== 1 ? "s" : ""}
                      </div>
                    );
                  }
                }

                return null;
              })()}

              {/* Status indicator - pool activity tarzında yeşil */}
              {activityStatus && (
                <div className="text-xs text-green-600">
                  {activityStatus === "empty"
                    ? "🔧 Configure forms"
                    : activityStatus === "partial"
                    ? "🔄 In progress"
                    : ""}
                </div>
              )}
            </div>
          )}

          {data.assigneeName && (
            <div className="text-xs text-blue-600 mt-1 text-center">
              Assigned to: {data.assigneeName}
            </div>
          )}
        </div>
      </div>

      {/* Delete Button - Sol üst köşe */}
      {data.onDelete && (
        <button
          onClick={handleDelete}
          className="absolute -top-2 -left-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg border-2 border-white"
          title={t("workflows:removeFromWorkflow")}
          style={{ zIndex: 1001 }}
        >
          <TrashIcon className="w-3 h-3" />
        </button>
      )}

      {/* Action Buttons */}
      {(nodeType === NodeType.ACTIVITY ||
        nodeType === NodeType.POOL_ACTIVITY) && (
        <div
          className="absolute -top-2 -right-2 flex space-x-1"
          style={{ zIndex: 1000 }}
        >
          {/* Edit Button (Left) - Always show for ACTIVITY and POOL_ACTIVITY nodes */}
          {data.onEdit && (
            <button
              onClick={handleEdit}
              className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg border-2 border-white"
              title="Edit Activity"
              style={{ zIndex: 1001 }}
            >
              <PencilIcon className="w-3 h-3" />
            </button>
          )}

          {/* View Button (Middle) - Only show for saved activities */}
          {isSavedActivity && data.onView && (
            <button
              onClick={handleView}
              className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors shadow-lg border-2 border-white"
              title={t("activities:viewActivityDetails")}
              style={{ zIndex: 1001 }}
            >
              <EyeIcon className="w-3 h-3" />
            </button>
          )}

          {/* Save as Template Button (Right) - Only show for ACTIVITY nodes (not POOL_ACTIVITY) with saved data */}
          {nodeType === NodeType.ACTIVITY &&
            isSavedActivity &&
            data.onSaveAsTemplate && (
              <button
                onClick={handleSaveAsTemplate}
                className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors shadow-lg border-2 border-white"
                title={t("workflows:saveAsTemplate")}
                style={{ zIndex: 1001 }}
              >
                <BookmarkIcon className="w-3 h-3" />
              </button>
            )}
        </div>
      )}

      {/* Legacy edit button for other node types */}
      {nodeType !== NodeType.ACTIVITY &&
        nodeType !== NodeType.POOL_ACTIVITY &&
        data.onEdit && (
          <button
            onClick={handleEdit}
            className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-blue-600 transition-colors"
          >
            ✎
          </button>
        )}

      {showSourceHandle && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className="w-3 h-3 !bg-gray-400"
        />
      )}
    </div>
  );
};

// Start Node Component
export const StartNode: React.FC<NodeProps<CustomNodeData>> = (props) => {
  return <CustomNode {...props} />;
};

// End Node Component
export const EndNode: React.FC<NodeProps<CustomNodeData>> = (props) => {
  return <CustomNode {...props} />;
};

// Activity Node Component (eski Task Node)
export const ActivityNode: React.FC<NodeProps<CustomNodeData>> = (props) => {
  return <CustomNode {...props} />;
};

// Pool Activity Node Component
export const PoolActivityNode: React.FC<NodeProps<CustomNodeData>> = (
  props
) => {
  return <CustomNode {...props} />;
};

export const nodeTypes = {
  START: StartNode,
  END: EndNode,
  ACTIVITY: ActivityNode,
  POOL_ACTIVITY: PoolActivityNode,
};
